import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SuppliersFiltersScreen.module.scss';
import Button from '../../../../../../common/components/button/Button';
import FormItem from '../../../../../../common/components/formItem/FormItem';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../../../../common/types/SelectValueLabel';
import { Controller, useForm } from 'react-hook-form';
import GenericTypesService from '../../../../../../api/genericTypes/GenericTypesService';
import SuppliersService from 'api/suppliers/SuppliersService';
import MaintenanceContractTypesService from 'api/maintenanceContractTypes/MaintenanceContractTypesService';
import Select from 'common/components/select/Select';
import Label from 'common/components/label/Label';
import Utils from 'common/services/Utils';

export interface Filters {
    typeIds?: string[];
    contractTypeIds?: string[];
    maintenanceTypeIds?: string[];
    allIn?: string;
    orderColumn?: string;
    orderBy?: string
    page?: number;
}

type Props = {
    filters: Filters;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
}

const SuppliersFiltersScreen: React.FC<Props> = ({ filters, onChange, onFilter }: Props) => {
    const { t } = useTranslation();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });

    const [typesOptions, setTypes] = useState<SelectValueLabel[]>([]);
    const [contractTypesOptions, setContractTypes] = useState<SelectValueLabel[]>([]);
    const [maintenanceTypesOptions, setMaintenanceTypes] = useState<SelectValueLabel[]>([]);

    const getData = async () => {
        const typesOptions = await GenericTypesService.catalogSupplier();
        const options = typesOptions.map(x => ({ ...x, name: x.label, label: x.isSystem ? t(`common.generic_types.${x.label}` as any) : x.label }));
        setTypes(Utils.sortAlphabetically(options));

        const contractTypes = await SuppliersService.listContractTypes();
        const contractOptions = contractTypes.map(x => ({
            value: x.supplierContractTypeId,
            label: x.isSystem ? t(`supplier_contract_types.${x.name}` as any) : x.name,
            name: x.name,
        } as SelectValueLabel));
        setContractTypes(Utils.sortAlphabetically(contractOptions));

        const maintenanceTypes = await MaintenanceContractTypesService.catalog();
        const maintenanceOptions = maintenanceTypes.map(x => ({
            ...x,
            name: x.label,
            label: x.isSystem ? t(`common.maintenance_contract_type.${x.label}` as any) : x.label,
        } as SelectValueLabel));
        setMaintenanceTypes(Utils.sortAlphabetically(maintenanceOptions));
    }

    useEffect(() => {
        getData();
    }, []);

    const clearFilters = () => {
        form.reset();
        form.setValue('typeIds', []);
        form.setValue('contractTypeIds', []);
        form.setValue('maintenanceTypeIds', []);
        onInputChange();
        onSubmit(form.getValues());
    }

    const onSubmit = (filters: Filters) => {
        onFilter(filters);
    }

    const onInputChange = () => {
        onChange(form.getValues());
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('suppliers.type')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            const selectedValues: string[] = Array.isArray(value) ? value : [];
                                            return (
                                                <Select
                                                    options={typesOptions}
                                                    isClearable
                                                    isMulti
                                                    placeholder={t('suppliers.type')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onChange((data ?? []).map(x => x.value));
                                                        onInputChange();
                                                    }}
                                                    value={typesOptions.filter(x => selectedValues.includes(x.value as string))}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="typeIds"
                                        defaultValue={form.getValues('typeIds') ?? []} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('supplier_contract_types.title')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            const selectedValues: string[] = Array.isArray(value) ? value : [];
                                            return (
                                                <Select
                                                    options={contractTypesOptions}
                                                    isClearable
                                                    isMulti
                                                    placeholder={t('supplier_contract_types.title')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onChange((data ?? []).map(x => x.value));
                                                        onInputChange();
                                                    }}
                                                    value={contractTypesOptions.filter(x => selectedValues.includes(x.value as string))}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="contractTypeIds"
                                        defaultValue={form.getValues('contractTypeIds') ?? []} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('suppliers.maintenance_type')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            const selectedValues: string[] = Array.isArray(value) ? value : [];
                                            return (
                                                <Select
                                                    options={maintenanceTypesOptions}
                                                    isClearable
                                                    isMulti
                                                    placeholder={t('suppliers.maintenance_type')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onChange((data ?? []).map(x => x.value));
                                                        onInputChange();
                                                    }}
                                                    value={maintenanceTypesOptions.filter(x => selectedValues.includes(x.value as string))}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="maintenanceTypeIds"
                                        defaultValue={form.getValues('maintenanceTypeIds') ?? []} />
                                </FormItem>
                            </Col>
                        </Row>
                    </FormItem>
                </div>
                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(SuppliersFiltersScreen);